import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, RefreshCw, Trash2, AlertCircle, CheckCircle, MessageSquare } from "lucide-react";
import { authFetch } from "@/utils/auth-fetch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TopicInfo {
  topicName: string;
  businessName: string;
  description: string;
  pendingMessages: number;
  messagesProcessed?: number;
  lastActivity?: string;
  consumerGroup?: string;
  lag?: number;
}

const topicMappings: Record<string, { businessName: string; description: string }> = {
  // Request topics
  'page-semantic-intent-agent': {
    businessName: 'Semantic Analysis Requests',
    description: 'Requests to analyze page content and extract meaning, topics, and intent'
  },
  'page-chatbot-agent': {
    businessName: 'Chatbot Conversation Requests',
    description: 'User questions and chatbot interaction requests'
  },
  'customer-intent-agent': {
    businessName: 'Customer Intent Analysis',
    description: 'Analyzing user behavior and determining customer intentions'
  },
  'site-crawler-agent': {
    businessName: 'Website Crawling Tasks',
    description: 'Requests to crawl and index website pages'
  },
  'orchestration-agent': {
    businessName: 'Workflow Orchestration',
    description: 'Managing multi-agent workflows and coordination'
  },
  
  // Response topics
  'page-semantic-intent-responses': {
    businessName: 'Semantic Analysis Results',
    description: 'Completed semantic analysis with topics, intents, and metadata'
  },
  'page-chatbot-responses': {
    businessName: 'Chatbot Responses',
    description: 'Chatbot answers and conversation responses'
  },
  'customer-intent-responses': {
    businessName: 'Customer Intent Results',
    description: 'Analyzed customer intentions and behavior insights'
  },
  'agent-responses': {
    businessName: 'General Agent Responses',
    description: 'Responses from various AI agents'
  },
  
  // Other important topics
  'suggestions-responses': {
    businessName: 'Suggestion Results',
    description: 'AI-generated suggestions and recommendations'
  },
  'page-role-agent': {
    businessName: 'Page Role Classification',
    description: 'Determining the purpose and role of web pages'
  },
  'adjacent-topics-agent': {
    businessName: 'Related Topics Discovery',
    description: 'Finding related topics and content connections'
  }
};

export default function KafkaMonitoring() {
  const [topics, setTopics] = useState<TopicInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cleaningTopic, setCleaningTopic] = useState<string | null>(null);
  const [showCleanDialog, setShowCleanDialog] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [showCleanAllDialog, setShowCleanAllDialog] = useState(false);
  const [initialSortOrder, setInitialSortOrder] = useState<string[]>([]);

  const fetchTopicInfo = async () => {
    try {
      setError(null);
      console.log('[KafkaMonitoring] Fetching from path: /admin/kafka/topics');
      const response = await authFetch('/admin/kafka/topics');
      
      if (!response.ok) {
        throw new Error('Failed to fetch Kafka topics');
      }

      const data = await response.json();
      
      // Transform the data to include business-friendly names
      const transformedTopics: TopicInfo[] = data.topics.map((topic: any) => {
        const mapping = topicMappings[topic.name] || {
          businessName: topic.name,
          description: 'Kafka topic for agent communication'
        };

        return {
          topicName: topic.name,
          businessName: mapping.businessName,
          description: mapping.description,
          pendingMessages: topic.highWaterMark - topic.lowWaterMark,
          messagesProcessed: topic.messagesProcessed || topic.highWaterMark || 0,
          lastActivity: topic.lastActivity || 'Unknown',
          consumerGroup: topic.consumerGroup,
          lag: topic.lag || 0
        };
      });

      // Sort by pending messages (highest first)
      transformedTopics.sort((a, b) => b.pendingMessages - a.pendingMessages);
      
      // If this is the initial load, save the sort order
      if (initialSortOrder.length === 0 && !refreshing) {
        setInitialSortOrder(transformedTopics.map(t => t.topicName));
      }
      
      // If we have an initial sort order, use it to maintain stable positions
      if (initialSortOrder.length > 0 && !refreshing) {
        transformedTopics.sort((a, b) => {
          const indexA = initialSortOrder.indexOf(a.topicName);
          const indexB = initialSortOrder.indexOf(b.topicName);
          
          // If both topics are in the initial order, maintain that order
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          
          // If a topic is new (not in initial order), put it at the end
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          
          return 0;
        });
      }
      
      setTopics(transformedTopics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Kafka topics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTopicInfo();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTopicInfo, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    // Reset the initial sort order on manual refresh
    setInitialSortOrder([]);
    fetchTopicInfo();
  };

  const handleCleanTopic = (topicName: string) => {
    setSelectedTopic(topicName);
    setShowCleanDialog(true);
  };

  const handleCleanAll = () => {
    setShowCleanAllDialog(true);
  };

  const confirmCleanTopic = async () => {
    if (!selectedTopic) return;

    setCleaningTopic(selectedTopic);
    setShowCleanDialog(false);

    try {
      const response = await authFetch(`/admin/kafka/topics/${selectedTopic}/clean`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to clean topic');
      }

      // Refresh the data
      await fetchTopicInfo();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clean topic');
    } finally {
      setCleaningTopic(null);
      setSelectedTopic(null);
    }
  };

  const confirmCleanAll = async () => {
    setShowCleanAllDialog(false);
    setCleaningTopic('all');

    try {
      const response = await authFetch('/admin/kafka/topics/clean-all', {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to clean all topics');
      }

      // Refresh the data
      await fetchTopicInfo();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clean all topics');
    } finally {
      setCleaningTopic(null);
    }
  };

  const totalPendingMessages = topics.reduce((sum, topic) => sum + topic.pendingMessages, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Kafka Message Queue Monitor</h1>
          <p className="text-muted-foreground mt-1">
            Monitor and manage pending messages in the AI agent communication system
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button
            variant="destructive"
            onClick={handleCleanAll}
            disabled={cleaningTopic !== null || totalPendingMessages === 0}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clean All Topics
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Queue Summary</CardTitle>
          <CardDescription>
            Overall status of the message processing system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Topics</p>
              <p className="text-2xl font-bold">{topics.length}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Pending Messages</p>
              <p className="text-2xl font-bold">{totalPendingMessages.toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Status</p>
              {totalPendingMessages === 0 ? (
                <Badge variant="success" className="mt-1">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  All Clear
                </Badge>
              ) : totalPendingMessages > 1000 ? (
                <Badge variant="destructive" className="mt-1">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  High Load
                </Badge>
              ) : (
                <Badge variant="warning" className="mt-1">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Processing
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Topics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {topics.map((topic) => (
          <Card key={topic.topicName} className={cleaningTopic === topic.topicName ? 'opacity-50' : ''}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <CardTitle className="text-base">{topic.businessName}</CardTitle>
                  <CardDescription className="text-xs">
                    {topic.description}
                  </CardDescription>
                </div>
                <Badge 
                  variant={topic.pendingMessages === 0 ? 'secondary' : topic.pendingMessages > 100 ? 'destructive' : 'default'}
                >
                  {topic.pendingMessages.toLocaleString()}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Technical Name:</span>
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">{topic.topicName}</code>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={`text-xs ${
                    topic.lastActivity === 'Empty' ? 'text-muted-foreground' : 
                    topic.lastActivity === 'Active' ? 'text-green-600' : 
                    'text-yellow-600'
                  }`}>
                    {topic.lastActivity}
                  </span>
                </div>
                {topic.consumerGroup && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Consumer Lag:</span>
                    <span>{topic.lag || 0}</span>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-3"
                  onClick={() => handleCleanTopic(topic.topicName)}
                  disabled={cleaningTopic !== null || topic.pendingMessages === 0}
                >
                  {cleaningTopic === topic.topicName ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-3 w-3 mr-2" />
                  )}
                  Clear Messages
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Clean Single Topic Dialog */}
      <Dialog open={showCleanDialog} onOpenChange={setShowCleanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Topic Messages</DialogTitle>
            <DialogDescription>
              Are you sure you want to clear all pending messages from{' '}
              <strong>{topics.find(t => t.topicName === selectedTopic)?.businessName}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCleanDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmCleanTopic}>
              Clear Messages
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clean All Topics Dialog */}
      <Dialog open={showCleanAllDialog} onOpenChange={setShowCleanAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear All Topic Messages</DialogTitle>
            <DialogDescription>
              Are you sure you want to clear all {totalPendingMessages.toLocaleString()} pending messages 
              from all topics? This will reset the entire message queue system. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCleanAllDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmCleanAll}>
              Clear All Messages
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}